import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { isUploadError } from '@services/api';

export type UploadStatus = 'uploading' | 'retrying' | 'offline' | 'success' | 'error';

export type UploadErrorKind = 'network' | 'tooLarge';

export interface UploadEntry {
  id: string;
  label: string;
  progress: number;
  status: UploadStatus;
  errorKind?: UploadErrorKind;
  onRetry?: () => void;
  onDismiss?: () => void;
}

interface State {
  uploads: UploadEntry[];
}

const UPLOAD_REMOVE_DELAY = 4000;
const RETRY_DELAYS_MS = [2000, 5000, 15000, 30000];

let count = 0;
const genId = () => {
  count += 1;
  return count.toString();
};

const removeTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const listeners: Array<(state: State) => void> = [];
let memoryState: State = { uploads: [] };

const setState = (updater: (state: State) => State) => {
  memoryState = updater(memoryState);
  listeners.forEach((listener) => listener(memoryState));
};

const addUpload = (label: string) => {
  const id = genId();
  setState((state) => ({
    uploads: [...state.uploads, { id, label, progress: 0, status: 'uploading' }],
  }));
  return id;
};

const updateUpload = (id: string, changes: Partial<UploadEntry>) => {
  setState((state) => ({
    uploads: state.uploads.map((upload) => (upload.id === id ? { ...upload, ...changes } : upload)),
  }));
};

const setProgress = (id: string, progress: number) => updateUpload(id, { progress });

const removeUpload = (id: string) => {
  setState((state) => ({ uploads: state.uploads.filter((upload) => upload.id !== id) }));
};

const removeAfterDelay = (id: string) => {
  if (removeTimeouts.has(id)) return;
  const timeout = setTimeout(() => {
    removeTimeouts.delete(id);
    removeUpload(id);
  }, UPLOAD_REMOVE_DELAY);
  removeTimeouts.set(id, timeout);
};

const settleUpload = (id: string, status: 'success' | 'error') => {
  updateUpload(id, { status, progress: 100 });
  removeAfterDelay(id);
};

const completeUpload = (id: string) => settleUpload(id, 'success');
const failUpload = (id: string) => settleUpload(id, 'error');

export const isUploadActive = ({ status, onRetry }: UploadEntry) =>
  status === 'uploading' || status === 'retrying' || status === 'offline' || Boolean(onRetry);

export const useUploadQueue = () => {
  const [state, setLocalState] = useState<State>(memoryState);

  useEffect(() => {
    listeners.push(setLocalState);
    return () => {
      const index = listeners.indexOf(setLocalState);
      if (index > -1) listeners.splice(index, 1);
    };
  }, []);

  return state;
};

export const uploadQueue = { addUpload, setProgress, completeUpload, failUpload };

const getUploadFiles = (body: FormData) =>
  Array.from(body.values()).filter((value): value is File => value instanceof File);

export const getUploadLabel = (body: FormData, fallback: string) => {
  const files = getUploadFiles(body);
  if (!files.length) return fallback;
  if (files.length === 1) return files[0].name;
  return `${files.length} files`;
};

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const waitForOnline = () =>
  new Promise<void>((resolve) => {
    if (navigator.onLine) resolve();
    else window.addEventListener('online', () => resolve(), { once: true });
  });

const waitForUserDecision = (id: string, errorKind: UploadErrorKind) =>
  new Promise<boolean>((resolve) => {
    updateUpload(id, {
      status: 'error',
      errorKind,
      onRetry: () => resolve(true),
      onDismiss: () => resolve(false),
    });
  });

const getConnectionType = () =>
  (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType;

export const trackedUpload = async <T extends { success?: boolean }>(
  body: FormData,
  fallbackLabel: string,
  send: (onProgress: (percent: number) => void) => Promise<T>,
): Promise<T> => {
  const files = getUploadFiles(body);
  const id = addUpload(getUploadLabel(body, fallbackLabel));
  const startedAt = Date.now();
  let attempts = 0;
  let autoRetries = 0;
  let maxProgress = 0;

  const track = (event: string, extra: Record<string, unknown> = {}) =>
    posthog?.capture(event, {
      files_count: files.length,
      size_mb: Math.round(files.reduce((sum, file) => sum + file.size, 0) / 1024 ** 2),
      max_progress: maxProgress,
      attempts,
      duration_s: Math.round((Date.now() - startedAt) / 1000),
      connection: getConnectionType(),
      online: navigator.onLine,
      ...extra,
    });

  for (;;) {
    attempts += 1;
    updateUpload(id, {
      status: 'uploading',
      progress: 0,
      errorKind: undefined,
      onRetry: undefined,
      onDismiss: undefined,
    });
    try {
      const resp = await send((percent) => {
        maxProgress = Math.max(maxProgress, percent);
        setProgress(id, percent);
      });
      if (attempts > 1) track('upload_recovered');
      if (resp?.success) completeUpload(id);
      else failUpload(id);
      return resp;
    } catch (err) {
      const status = isUploadError(err) ? err.status : undefined;
      const retryable = isUploadError(err) && err.retryable;

      if (retryable && autoRetries < RETRY_DELAYS_MS.length) {
        updateUpload(id, { status: 'retrying' });
        await wait(RETRY_DELAYS_MS[autoRetries]);
        autoRetries += 1;
        if (!navigator.onLine) {
          updateUpload(id, { status: 'offline' });
          await waitForOnline();
        }
        continue;
      }

      track('upload_failed', { status, error: (err as Error).message });

      if (status === 413) {
        updateUpload(id, {
          status: 'error',
          errorKind: 'tooLarge',
          onDismiss: () => removeUpload(id),
        });
        throw err;
      }

      const shouldRetry = await waitForUserDecision(id, 'network');
      if (!shouldRetry) {
        track('upload_dismissed');
        removeUpload(id);
        throw err;
      }
      autoRetries = 0;
    }
  }
};
